/**
 * Modal para inscribir una persona como miembro de un club
 */

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Select, Input, SectionBadge } from "@/components/ui";
import { X, Loader2, UserPlus, Flag, Mail, Users } from "lucide-react";
import { useCreateMember } from "@/hooks/usePersons";
import { useClubs, useUnitsByClub } from "@/hooks/useOrganization";
import { usePositions, useAcademicPeriods } from "@/hooks/useCatalogs";
import { useAuthStore } from "@/stores/authStore";
import { filterManageableClubs } from "@/lib/permissions";
import type { Person, Club, MemberRole, MemberClass, ClubType } from "@/types";
import {
  MEMBER_ROLE_LABELS,
  MEMBER_CLASS_LABELS,
  CLUB_TYPE_LABELS,
} from "@/types";
import { Timestamp } from "firebase/firestore";
import {
  getClubTypeByAge,
  getAllowedClubTypesByAge,
  resolveSectionForClub,
  getActiveSections,
} from "@/lib/utils";

const membershipSchema = z.object({
  clubId: z.string().min(1, "Debes seleccionar un club"),
  periodId: z.string().min(1, "Debes seleccionar un periodo"),
  positionId: z.string().optional(),
  unitId: z.string().optional(),
  role: z.string().min(1, "Debes seleccionar un cargo"),
  clase: z.string().optional(),
  email: z.string().email("Email inválido").optional().or(z.literal("")),
});

type MembershipForm = z.infer<typeof membershipSchema>;

interface EnrollMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  person: Person;
}

// Clases disponibles según tipo de club
const classesByClubType: Record<ClubType, MemberClass[]> = {
  aventureros: [
    "abejita_laboriosa",
    "rayito_de_sol",
    "constructor",
    "manos_ayudadoras",
  ],
  conquistadores: [
    "amigo",
    "companero",
    "explorador",
    "orientador",
    "viajero",
    "guia",
  ],
  guias_mayores: [
    "guia_mayor",
    "guia_mayor_master",
    "guia_mayor_master_avanzado",
  ],
};

// Roles disponibles para miembros
const availableRoles: MemberRole[] = [
  "member",
  "director",
  "subdirector",
  "consejero",
  "instructor",
  "tesorero",
  "secretario",
];

export default function EnrollMemberModal({
  isOpen,
  onClose,
  person,
}: EnrollMemberModalProps) {
  const { user, profile } = useAuthStore();
  const createMemberMutation = useCreateMember();

  const { data: clubs, isLoading: loadingClubs } = useClubs();
  const { data: positions } = usePositions();
  const { data: periods } = useAcademicPeriods();

  const [selectedClub, setSelectedClub] = useState<Club | null>(null);

  // Solo clubes que el usuario puede gestionar (RBAC por scope)
  const manageableClubs = filterManageableClubs(
    clubs,
    profile?.roleAssignments,
  );

  // Calcular edad y tipo de club permitido
  const personAge = (() => {
    if (!person.birthDate) return null;
    const birthDate =
      "toDate" in person.birthDate
        ? person.birthDate.toDate()
        : new Date(person.birthDate as unknown as string);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    return age;
  })();

  const allowedClubType =
    personAge !== null ? getClubTypeByAge(personAge) : null;
  const allowedClubTypes =
    personAge !== null ? getAllowedClubTypesByAge(personAge) : [];

  const filteredClubs =
    manageableClubs.filter((c) =>
      allowedClubTypes.length > 0
        ? getActiveSections(c).some((s) =>
            (allowedClubTypes as string[]).includes(s),
          )
        : true,
    ) ?? [];

  // Query de unidades dinámico basado en club seleccionado
  const { data: units, isLoading: loadingUnits } = useUnitsByClub(
    selectedClub?.id || "",
  );

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<MembershipForm>({
    resolver: zodResolver(membershipSchema),
    defaultValues: {
      role: "member",
      email: person.email || "",
    },
  });

  const watchClubId = watch("clubId");

  // Cuando cambia el club seleccionado
  useEffect(() => {
    if (watchClubId && clubs) {
      const club = clubs.find((c) => c.id === watchClubId);
      setSelectedClub(club || null);
    } else {
      setSelectedClub(null);
    }
  }, [watchClubId, clubs]);

  /**
   * Sección en la que quedará la persona en ESTE club: la edad manda, pero
   * respetando qué secciones tiene abiertas el club (resolveSectionForClub).
   * Es exactamente lo que se guarda al enviar — por eso el chip la muestra en
   * vez de un valor cableado.
   */
  const seccionResultante: ClubType | null = (() => {
    if (!selectedClub) return null;
    if (!person.birthDate) return (selectedClub.type as ClubType) ?? null;
    const b = person.birthDate as unknown as { toDate?: () => Date };
    const fecha =
      typeof b?.toDate === "function"
        ? b.toDate()
        : new Date(String(person.birthDate));
    return (resolveSectionForClub(fecha, selectedClub) as ClubType) ?? null;
  })();

  const onSubmit = async (data: MembershipForm) => {
    if (!user || !selectedClub) return;

    const selectedPosition = data.positionId
      ? positions?.find((p) => p.id === data.positionId)
      : null;

    const derivedSection = person.birthDate
      ? resolveSectionForClub(
          "toDate" in (person.birthDate as any)
            ? (person.birthDate as any).toDate()
            : new Date(String(person.birthDate)),
          selectedClub,
        )
      : selectedClub.type;

    const memberData = {
      personId: person.id,
      clubId: data.clubId,
      periodId: data.periodId,
      churchId: selectedClub.churchId || "",
      associationId: selectedClub.associationId || "",
      section: derivedSection as ClubType,
      status: "active" as const,
      positionId: data.positionId,
      position: selectedPosition?.name,
      registrationDate: Timestamp.now(),
      registrationPaid: false,
      role: data.role as MemberRole,
      clase: data.clase as MemberClass | undefined,
      roleStartDate: Timestamp.now(),
      // Datos desnormalizados para listas rápidas
      personName: person.fullName,
      personPhoto: person.photoUrl || "",
    };

    // Agregar unitId solo si tiene valor
    if (data.unitId) {
      (memberData as any).unitId = data.unitId;
    }

    try {
      await createMemberMutation.mutateAsync({
        data: memberData as any,
        userId: user.uid,
      });
      reset();
      onClose();
    } catch (error) {
      console.error("Error creating membership:", error);
    }
  };

  if (!isOpen) return null;

  // Filtrar posiciones por nivel de club
  const clubPositions = positions?.filter(
    (p) => p.level === "club" && p.status === "active",
  );

  // Obtener periodos activos
  const activePeriods = periods?.filter((p) => p.status === "active");

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl flex items-center justify-center shadow-md shadow-primary-500/25">
              <UserPlus className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Inscribir a Club
              </h2>
              <p className="text-sm text-gray-500">{person.fullName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {/* Información de la persona */}
          <div className="bg-gradient-to-r from-gray-50 to-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center gap-3">
              {person.photoUrl ? (
                <img
                  src={person.photoUrl}
                  alt={person.fullName}
                  className="w-12 h-12 rounded-xl object-cover shadow-sm"
                />
              ) : (
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-medium shadow-sm">
                  {person.firstName?.charAt(0)}
                  {person.lastName?.charAt(0)}
                </div>
              )}
              <div>
                <p className="font-medium text-gray-900">{person.fullName}</p>
                <p className="text-sm text-gray-500">
                  {person.idDocumentType} {person.idDocumentNumber}
                </p>
              </div>
            </div>
          </div>

          {/* Selección de Club */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Seleccionar Club *
            </label>
            {loadingClubs ? (
              <div className="flex items-center gap-2 text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Cargando clubes...
              </div>
            ) : (
              <Select {...register("clubId")} error={!!errors.clubId}>
                <option value="">Seleccionar club...</option>
                {filteredClubs.map((club) => (
                  <option key={club.id} value={club.id}>
                    {club.name} -{" "}
                    {getActiveSections(club)
                      .map((s) => CLUB_TYPE_LABELS[s as ClubType])
                      .join(", ")}
                  </option>
                ))}
              </Select>
            )}
            {errors.clubId && (
              <p className="text-red-500 text-xs mt-1">
                {errors.clubId.message}
              </p>
            )}
            {allowedClubType && personAge !== null && (
              <p className="text-xs text-blue-600 mt-1">
                📋 Por edad ({personAge} años), corresponde a{" "}
                {CLUB_TYPE_LABELS[allowedClubType]}
                {allowedClubTypes.length > 1 && (
                  <>
                    {" "}
                    — también puede ir a{" "}
                    {allowedClubTypes
                      .filter((s) => s !== allowedClubType)
                      .map((s) => CLUB_TYPE_LABELS[s])
                      .join(", ")}
                  </>
                )}
              </p>
            )}
            {!person.birthDate && (
              <p className="text-xs text-amber-600 mt-1">
                Sin fecha de nacimiento registrada. Se muestran todos los clubs.
              </p>
            )}
          </div>

          {/* Info del club seleccionado */}
          {selectedClub && (
            <div className="bg-gradient-to-r from-gray-50 to-white rounded-xl p-4 border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">
                    {selectedClub.name}
                  </p>
                  <p className="text-sm text-gray-500">
                    {selectedClub.meetingDay} {selectedClub.meetingTime}
                  </p>
                </div>
                {/* La sección REAL en la que va a quedar: la misma que se
                    guarda al enviar (resolveSectionForClub por edad + secciones
                    activas del club). Antes mostraba `club.type` —un campo
                    legado de cuando un club era de un solo tipo— y en los
                    clubes multi-sección caía a un "conquistadores" cableado:
                    a una persona de 23 años el chip le decía Conquistadores
                    mientras el sistema la guardaba en Guías Mayores. */}
                {seccionResultante && (
                  <SectionBadge section={seccionResultante} size="sm" />
                )}
              </div>
            </div>
          )}

          {/* Selección de Periodo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Periodo Académico *
            </label>
            <Select {...register("periodId")} error={!!errors.periodId}>
              <option value="">Seleccionar periodo...</option>
              {activePeriods?.map((period) => (
                <option key={period.id} value={period.id}>
                  {period.name} ({period.year})
                </option>
              ))}
            </Select>
            {errors.periodId && (
              <p className="text-red-500 text-xs mt-1">
                {errors.periodId.message}
              </p>
            )}
          </div>

          {/* Selección de Unidad (opcional) */}
          {selectedClub && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center gap-2">
                  <Flag className="w-4 h-4" />
                  Unidad / Patrulla (opcional)
                </div>
              </label>
              {loadingUnits ? (
                <div className="flex items-center gap-2 text-gray-500 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Cargando unidades...
                </div>
              ) : units && units.length > 0 ? (
                <Select {...register("unitId")}>
                  <option value="">Sin asignar a unidad</option>
                  {units
                    .filter((u) => u.status === "active")
                    .map((unit) => (
                      <option key={unit.id} value={unit.id}>
                        {unit.name} {unit.motto ? `- "${unit.motto}"` : ""}
                      </option>
                    ))}
                </Select>
              ) : (
                <p className="text-sm font-medium text-gray-500 bg-gradient-to-r from-gray-50 to-gray-100 p-3 rounded-xl border border-gray-100">
                  Este club no tiene unidades creadas. Puedes inscribir sin
                  unidad y asignar después.
                </p>
              )}
            </div>
          )}

          {/* Selección de Cargo (opcional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cargo (opcional)
            </label>
            <Select {...register("positionId")}>
              <option value="">Sin cargo específico</option>
              {clubPositions?.map((position) => (
                <option key={position.id} value={position.id}>
                  {position.name}
                </option>
              ))}
            </Select>
          </div>

          {/* Cargo en el Club */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Cargo en el Club *
              </div>
            </label>
            <Select {...register("role")} error={!!errors.role}>
              {availableRoles.map((role) => (
                <option key={role} value={role}>
                  {MEMBER_ROLE_LABELS[role]}
                </option>
              ))}
            </Select>
            {errors.role && (
              <p className="text-red-500 text-xs mt-1">{errors.role.message}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Define el cargo de la persona dentro del club
            </p>
          </div>

          {/* Clase/Nivel (según tipo de club) */}
          {selectedClub && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Clase / Nivel
              </label>
              <Select {...register("clase")}>
                <option value="">Sin clase asignada</option>
                {(allowedClubType
                  ? classesByClubType[allowedClubType]
                  : classesByClubType[selectedClub.type || "conquistadores"]
                )?.map((clase: string) => (
                  <option key={clase} value={clase}>
                    {MEMBER_CLASS_LABELS[clase as MemberClass]}
                  </option>
                ))}
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Nivel de progresión del miembro
              </p>
            </div>
          )}

          {/* Email para vinculación */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email para vincular cuenta (opcional)
              </div>
            </label>
            <Input
              type="email"
              {...register("email")}
              placeholder="correo@ejemplo.com"
              error={!!errors.email}
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">
                {errors.email.message}
              </p>
            )}
            {!errors.email &&
              user?.email &&
              watch("email")?.trim().toLowerCase() ===
                user.email.toLowerCase() && (
                <p className="text-amber-600 text-xs mt-1">
                  ⚠️ Este es tu propio correo. Si registras a otra persona,
                  déjalo vacío para que quede como menor a tu cargo.
                </p>
              )}
            <p className="text-xs text-gray-500 mt-1">
              Si el miembro se registra en la app con este email, se vinculará
              automáticamente
            </p>
          </div>

          {/* Botones */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || createMemberMutation.isPending}
            >
              {isSubmitting || createMemberMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Inscribiendo...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Inscribir al Club
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
